import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AppHeader from '../components/AppHeader';
import ActionPlanTab from '../components/ActionPlanTab';
import ProgressTab from '../components/ProgressTab';
import ResourcesTab from '../components/ResourcesTab';
import RealityCheck from '../components/RealityCheck';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('action-plan');
  const [realityDismissed, setRealityDismissed] = useState(false);

  useEffect(() => {
    const fetchPlan = async () => {
      const sessionId = location.state?.sessionId || localStorage.getItem('creditSessionId');
      
      if (!sessionId) {
        console.log('No session ID found, redirecting to home');
        navigate('/');
        return;
      }

      // Try fetching plan with retries
      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          console.log(`Fetching plan for session (attempt ${attempt}):`, sessionId);
          const response = await axios.get(`${API}/plan/${sessionId}`);
          console.log('Plan response status:', response.data?.status);
          
          // Plan fetched successfully — auto-unlock happens on the backend
          setPlan(response.data);
          localStorage.setItem('creditSessionId', sessionId);
          setLoading(false);
          return;
        } catch (error) {
          console.error(`Attempt ${attempt} failed:`, error.response?.status, error.message);
          
          if (attempt < 3) {
            // Wait before retrying (plan might still be writing to DB)
            await new Promise(resolve => setTimeout(resolve, 2000));
          }
        }
      }
      
      // All 3 API attempts failed — use planData from navigation state if available
      const statePlan = location.state?.planData;
      if (statePlan) {
        console.log('Using plan data from navigation state');
        setPlan(statePlan);
        setLoading(false);
      } else {
        // Last resort: show error with retry option instead of redirecting
        console.error('All fetch attempts failed and no state plan available');
        setLoading(false);
        setPlan(null);
      }
    };

    fetchPlan();
  }, [location, navigate]);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-box">
          <div className="spinner"></div>
          <div className="loading-text">Loading your dashboard...</div>
        </div>
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="loading-container">
        <div className="loading-box">
          <div className="loading-text" style={{ marginBottom: 16 }}>Your plan is being prepared</div>
          <button 
            className="btn-primary"
            onClick={() => window.location.reload()}
            style={{ padding: '12px 32px', borderRadius: 8, background: '#16a34a', color: '#fff', border: 'none', fontSize: 16, cursor: 'pointer' }}
            data-testid="retry-btn"
          >
            Refresh
          </button>
          <button 
            onClick={() => navigate('/')}
            style={{ marginTop: 12, padding: '10px 24px', background: 'transparent', color: '#94a3b8', border: '1px solid #334155', borderRadius: 8, cursor: 'pointer', fontSize: 14 }}
            data-testid="go-home-btn"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container">
      <AppHeader userName="Member" />
      
      <div className="tab-navigation">
        <button
          className={`tab-nav-btn ${activeTab === 'action-plan' ? 'active' : ''}`}
          onClick={() => setActiveTab('action-plan')}
          data-testid="tab-action-plan"
        >
          <span className="tab-icon">&#9889;</span>
          <span className="tab-label">Fast-Track Plan</span>
        </button>
        
        <button
          className={`tab-nav-btn ${activeTab === 'progress' ? 'active' : ''}`}
          onClick={() => setActiveTab('progress')}
          data-testid="tab-progress"
        >
          <span className="tab-icon">📊</span>
          <span className="tab-label">Progress</span>
        </button>
        
        <button
          className={`tab-nav-btn ${activeTab === 'resources' ? 'active' : ''}`}
          onClick={() => setActiveTab('resources')}
          data-testid="tab-resources"
        >
          <span className="tab-icon">📚</span>
          <span className="tab-label">Resources</span>
        </button>
      </div>

      <div className="dashboard-body">
        {/* Reality Check — shows above plan when goals are unrealistic */}
        {activeTab === 'action-plan' && !realityDismissed && plan?.reality_check?.triggered && (
          <RealityCheck plan={plan} onOptionSelect={(opt) => { if (opt === 'fastest') setRealityDismissed(true); }} />
        )}
        {activeTab === 'action-plan' && <ActionPlanTab plan={plan} />}
        {activeTab === 'progress' && <ProgressTab plan={plan} />}
        {activeTab === 'resources' && <ResourcesTab />}
      </div>
    </div>
  );
};

export default DashboardPage;
